<template>
  <div ref="selectorRootRef">
    <label class="block text-sm font-bold text-black mb-1">
      Couleurs <span class="text-red-500">*</span>
    </label>

    <!-- Input déclencheur → ouvre la modal -->
    <div class="relative">
      <button
        type="button"
        class="w-full flex items-center gap-2 px-3 py-2 border rounded-md bg-white text-left text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        :class="
          showInvalid
            ? 'border-red-500 ring-1 ring-red-500'
            : 'border-gray-300'
        "
        @click="openModal"
        @blur="onTriggerBlur"
      >
        <i class="fas fa-search text-gray-400 text-xs"></i>
        <span class="text-gray-400 truncate">
          Rechercher et ajouter des couleurs...
        </span>
      </button>
      <p
        v-if="showInvalid"
        class="absolute left-0 top-full z-[200] mt-0.5 text-xs text-red-600 bg-white border border-red-200 rounded px-1.5 py-0.5 shadow-md whitespace-nowrap pointer-events-none"
        role="alert"
      >
        Ce champ est obligatoire
      </p>
    </div>

    <!-- Liste des couleurs ajoutées -->
    <div v-if="modelValue.length > 0" class="mt-2 flex flex-wrap gap-2">
      <div
        v-for="(couleur, index) in modelValue"
        :key="index"
        :class="[
          'inline-flex items-center gap-1.5 px-2.5 py-1.5 border-2 rounded-full transition-colors text-sm',
          props.allowPrincipal !== false
            ? 'cursor-pointer hover:bg-gray-100'
            : 'bg-gray-50',
          couleur.isMain && props.allowPrincipal !== false
            ? 'bg-blue-50 border-blue-500'
            : 'bg-gray-50 border-gray-200',
        ]"
        @click="
          props.allowPrincipal !== false && !couleur.isMain
            ? setCouleurPrincipale(index)
            : null
        "
      >
        <span
          class="color-swatch-dot inline-block w-4 h-4 rounded-full flex-shrink-0"
          :style="getColorSwatchStyle(couleur.nom, couleur.hexColor)"
        ></span>
        <span class="font-medium text-black whitespace-nowrap">
          {{ couleur.nom }}
        </span>
        <span
          v-if="linkedCaptionFor(couleur.id)"
          class="text-xs text-gray-500 whitespace-nowrap"
        >
          {{ linkedCaptionFor(couleur.id) }}
        </span>
        <button
          type="button"
          @click.stop="removeCouleur(index)"
          class="ml-0.5 transition-colors text-red-500 hover:text-red-700"
          title="Supprimer cette couleur"
        >
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
    </div>

    <!-- Quantités par taille (couleurs mélangées) -->
    <div
      v-if="
        modelValue.length > 0 &&
        showQuantitesParTaille &&
        taillesList &&
        taillesList.length > 0
      "
      class="mt-3 space-y-2"
    >
      <div
        v-for="(couleur, index) in modelValue"
        :key="'qty-' + index"
        class="flex items-center gap-3 px-2 py-1 rounded-md bg-gray-50"
      >
        <div class="flex items-center gap-2 flex-shrink-0 w-28">
          <span
            class="color-swatch-dot inline-block w-3 h-3 rounded-full flex-shrink-0"
            :style="getColorSwatchStyle(couleur.nom, couleur.hexColor)"
          ></span>
          <span class="text-xs font-medium text-black truncate">{{
            couleur.nom
          }}</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(taille, tailleIndex) in taillesList"
            :key="tailleIndex"
            class="flex items-center gap-1"
          >
            <label class="text-xs text-black">{{ taille }}</label>
            <input
              type="number"
              data-color-quantity
              :value="getQuantiteParTaille(couleur, tailleIndex)"
              @input="updateQuantiteParTaille(index, tailleIndex, $event)"
              min="0"
              placeholder="0"
              class="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="modelValue.length === 0" class="mt-2 text-sm text-black italic">
      Aucune couleur ajoutée. Cliquez ci-dessus pour en choisir.
    </div>

    <CouleursPickerModal
      v-model:isVisible="isModalOpen"
      :couleursList="couleursList"
      :selectedIds="modelValue.map((c) => c.id)"
      :official-only="officialOnly"
      :linked-source-labels-by-color-id="linkedSourceLabelsByColorId"
      @confirm="onModalConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import type { CouleurSelectorItem, CouleurAPI } from "@/types/mel-components";
import { getColorSwatchStyle } from "@/utils/colorFamilies";

interface Props {
  modelValue: CouleurSelectorItem[];
  couleursList: CouleurAPI[];
  allowPrincipal?: boolean;
  taillesList?: string[];
  forceValidate?: boolean;
  /** Si true (défaut), le picker ne propose que les couleurs officielles. */
  officialOnly?: boolean;
  /** Libellés Shopify liés par id couleur officielle. */
  linkedSourceLabelsByColorId?: Record<number, string[]>;
}

interface Emits {
  (e: "update:modelValue", value: CouleurSelectorItem[]): void;
  (e: "manage-colors"): void;
  (e: "selection-complete"): void;
}

const props = withDefaults(defineProps<Props>(), {
  allowPrincipal: true,
  forceValidate: false,
  officialOnly: true,
});
const emit = defineEmits<Emits>();

const isModalOpen = ref(false);
const touched = ref(false);
const selectorRootRef = ref<HTMLElement | null>(null);

const showInvalid = computed(
  () =>
    props.modelValue.length === 0 &&
    (touched.value || props.forceValidate),
);

const onTriggerBlur = () => {
  touched.value = true;
};

const openModal = () => {
  isModalOpen.value = true;
};

const showQuantitesParTaille = computed(() => {
  return props.taillesList && props.taillesList.length > 0;
});

const linkedCaptionFor = (colorId: number | string) => {
  const id = Number(colorId);
  if (!Number.isInteger(id) || id <= 0) return "";
  const labels = props.linkedSourceLabelsByColorId?.[id] ?? [];
  if (!labels.length) return "";
  return `liée : ${labels.join(", ")}`;
};

const onModalConfirm = (colors: CouleurAPI[]) => {
  const previousById = new Map(
    props.modelValue.map((c) => [parseInt(String(c.id), 10), c]),
  );

  const updated = colors.map((couleur) => {
    const id = parseInt(String(couleur.id_couleur), 10);
    const prev = previousById.get(id);
    if (prev) return { ...prev };

    return {
      id,
      nom: couleur.couleur_FR,
      isMain: false,
      hexColor: couleur.hexColor || null,
      quantitesParTaille:
        showQuantitesParTaille.value && props.taillesList
          ? new Array(props.taillesList.length).fill(0)
          : undefined,
    };
  });

  if (updated.length > 0 && !updated.some((c) => c.isMain)) {
    updated[0].isMain = true;
  }

  emit("update:modelValue", updated);
  emit("selection-complete");
};

const focusFirstQuantity = () => {
  const firstQuantityInput =
    selectorRootRef.value?.querySelector<HTMLInputElement>(
      "[data-color-quantity]",
    );
  firstQuantityInput?.focus();
  firstQuantityInput?.select();
  return Boolean(firstQuantityInput);
};

defineExpose({ focusFirstQuantity });

const setCouleurPrincipale = (index: number) => {
  const updatedCouleurs = props.modelValue.map((couleur, i) => ({
    ...couleur,
    isMain: i === index,
  }));
  emit("update:modelValue", updatedCouleurs);
};

const removeCouleur = (index: number) => {
  const wasMain = props.modelValue[index].isMain;
  const updatedCouleurs = [...props.modelValue];
  updatedCouleurs.splice(index, 1);

  if (wasMain && updatedCouleurs.length > 0) {
    updatedCouleurs[0].isMain = true;
  }

  emit("update:modelValue", updatedCouleurs);
};

const getQuantiteParTaille = (
  couleur: CouleurSelectorItem,
  tailleIndex: number,
): number => {
  if (
    !couleur.quantitesParTaille ||
    !Array.isArray(couleur.quantitesParTaille)
  ) {
    return 0;
  }
  return couleur.quantitesParTaille[tailleIndex] || 0;
};

const updateQuantiteParTaille = (
  couleurIndex: number,
  tailleIndex: number,
  event: Event,
) => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;

  if (value < 0) {
    target.value = "0";
    return;
  }

  const updatedCouleurs = [...props.modelValue];
  const couleur = updatedCouleurs[couleurIndex];

  if (!couleur.quantitesParTaille && props.taillesList) {
    couleur.quantitesParTaille = new Array(props.taillesList.length).fill(0);
  }

  if (couleur.quantitesParTaille) {
    couleur.quantitesParTaille[tailleIndex] = value;
  }

  emit("update:modelValue", updatedCouleurs);
};

watch(
  () => props.taillesList,
  (newTailles, oldTailles) => {
    if (!showQuantitesParTaille.value) return;

    if (newTailles && oldTailles && newTailles.length !== oldTailles.length) {
      const updatedCouleurs = props.modelValue.map((couleur) => ({
        ...couleur,
        quantitesParTaille: newTailles
          ? new Array(newTailles.length).fill(0)
          : undefined,
      }));
      emit("update:modelValue", updatedCouleurs);
    }
  },
  { deep: true },
);

watch(
  () => showQuantitesParTaille.value,
  (shouldShow) => {
    if (shouldShow && props.taillesList && props.taillesList.length > 0) {
      const updatedCouleurs = props.modelValue.map((couleur) => {
        if (
          !couleur.quantitesParTaille ||
          couleur.quantitesParTaille.length !== props.taillesList!.length
        ) {
          return {
            ...couleur,
            quantitesParTaille: new Array(props.taillesList!.length).fill(0),
          };
        }
        return couleur;
      });
      emit("update:modelValue", updatedCouleurs);
    }
  },
);
</script>

<style scoped>
.color-swatch-dot {
  display: inline-block;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-clip: padding-box;
}
</style>
